// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, TooltipHost, Icon } from 'office-ui-fabric-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';

import { logPageAction } from 'src/lib/ApplicationInsights';
import Styles from './ManageBulkUsersModal.module.scss';
import getColumnHeadersList from './columnHeaders';
import {
    postAddBulkAadUsers,
    addBulkAadUsersStatusSelector,
} from './../../store/manageAadOps';
import { BulkRetailUserType } from './../../views/manageBulkUsers/manageBulkUsers';

interface ExportRetailUser {
    firstName?: string;
    lastName?: string;
    email?: string;
    roleName?: string;
    channelName?: string;
    tierName?: string | number;
    soldToCustomerNbr?: string | number;
    shiptoCustomerNbr?: string | number;
    isWarrantyUpdateEnabled?: string;
    isActive?: string;
    errorInfo?: string;
    inviteRedeemUrl?: string;
}

export default function ManageBulkUsersModal(props: any) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { bulkRetailUsersList } = props;
    const exportFileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const exportFileName = `BulkUploadUsersStatus.xlsx`;

    const [showModal, setShowModal] = useState<boolean>(false);
    const [processingStatus, setProcessingStatus] = useState(0);
    const [processedCount, setProcessedCount] = useState(0);
    const [showExportOption, setShowExportOption] = useState<boolean>(false);

    const actionResponse = useSelector(state => addBulkAadUsersStatusSelector(state));
    const validRowsCount = bulkRetailUsersList.filter((x: any) => x.action !== '').length;

    const exportExcelColumnHeader: any = {
        firstName: t('first_name'),
        lastName: t('last_name'),
        email: t('email'),
        roleName: t('role'),
        channelName: t('channel'),
        tierName: t('tier_name'),
        soldToCustomerNbr: t('sold_to'),
        shiptoCustomerNbr: t('ship_to'),
        isWarrantyUpdateEnabled: t('Enable Warranty Update'),
        isActive: t('user_status'),
        errorInfo: t('error_info'),
        inviteRedeemUrl: t('invite_redeem_url'),
    };

    function displayModal(event: any) {
        setShowModal(true);
        event.preventDefault();
        return false;
    }

    function closeModal(event: any) {
        setShowModal(false);
        event.preventDefault();
        return false;
    }

    function capitalize(str: string) {
        return str !== undefined && str !== null && str !== '' ? str.charAt(0).toUpperCase() + str.slice(1) : '';
    }

    function getColumnHeader(index: number, column: any) {
        let returnElement: any;
        returnElement = <th key={index} role="gridcell" className={column.visible ? Styles[column.className] : Styles.hidden} style={{ width: column.width }}>
            <div className={column.visible ? Styles[column.className] : Styles.hidden} style={{ width: column.width }}>
                {capitalize(column.name)}
            </div>
        </th>;

        return returnElement;
    }

    function getColumnContent(index: number, column: any, element: BulkRetailUserType) {
        let returnElement: any;
        const colData = element.retailUser[column.fieldName] ? element.retailUser[column.fieldName] : '';
        returnElement = <td key={index} role="gridcell" className={column.visible ? Styles[column.className] : Styles.hidden} style={{ width: column.width }}>
            <div className={column.visible ? Styles[column.className] : Styles.hidden} style={{ width: column.width }}>
                {colData.length > column.maxChar ?
                    <TooltipHost
                        hostClassName={Styles.elementToolTip}
                        aria-label={colData}
                        content={colData}>
                        {colData.length > column.maxChar ? `${colData.substring(0, column.maxChar - 3)}...` : colData}
                    </TooltipHost> : colData}
            </div>
        </td>;
        return returnElement;
    }

    function getStatusSuccessContent(element: BulkRetailUserType) {
        let msg = '';
        if (element.errorInfo !== '') {
            msg = element.errorInfo;
        }
        else if (element.action.toLowerCase() === 'delete') {
            msg = t('user_deleted_successfully');
        }
        else if (element.action.toLowerCase() === 'update') {
            msg = t('user_updated_successfully');
        }
        else {
            msg = t('user_created_successfully');
        }
        return msg;
    }

    const isValidUserResponse = (response: any) => {
        let status = false;
        if (!response || response === null || response.toString().trim() === '') {
            status = false;
        }
        else if (bulkRetailUsersList[response.rowIndex]?.retailUser?.email?.toLowerCase() !== response?.retailUser?.email?.toLowerCase()) {
            status = false;
        }
        else {
            status = true;
        }
        return status;
    };

    useEffect(() => {
        if (actionResponse && actionResponse !== null && actionResponse !== '' && !(actionResponse.length === 0)) {
            if (isValidUserResponse(actionResponse)) {
                if (actionResponse.errorInfo.trim() === '' && actionResponse.action.trim().toLowerCase() === 'add') {
                    if (actionResponse.retailUser.inviteRedeemUrl && actionResponse.retailUser.inviteRedeemUrl.trim() !== '') {
                        bulkRetailUsersList[actionResponse.rowIndex].retailUser.inviteRedeemUrl = actionResponse.retailUser.inviteRedeemUrl;
                    }
                    else {
                        bulkRetailUsersList[actionResponse.rowIndex].retailUser.inviteRedeemUrl = t('user_exists_on_robo_group');
                    }
                    bulkRetailUsersList[actionResponse.rowIndex].retailUser.aadUserId = actionResponse.retailUser.aadUserId;
                }
                bulkRetailUsersList[actionResponse.rowIndex].status = actionResponse.status;
                bulkRetailUsersList[actionResponse.rowIndex].errorInfo = actionResponse.errorInfo;

                const count = processedCount + 1;
                setProcessedCount(count);
                if (count === bulkRetailUsersList.length) {
                    setProcessingStatus(2);
                    if (bulkRetailUsersList.some((user: any) => user && (user.action?.toLowerCase() === 'add' || user.action?.toLowerCase() === 'status') && user.status?.toLowerCase() !== 'error')) {
                        setShowExportOption(true);
                    }
                    else {
                        setShowExportOption(false);
                    }
                }
                else {
                    setShowExportOption(false);
                }
            }
            else {
                setShowExportOption(false);
            }
        }
    }, [actionResponse]);

    const processBulkRetailUsersList = () => {
        logPageAction('Bulk User Upload - Process File is clicked');
        setProcessingStatus(1);
        let rowIndex = 0;
        bulkRetailUsersList.forEach((bulkRetailUser: any) => {
            if (bulkRetailUser.errorInfo === '') {
                bulkRetailUsersList[rowIndex].status = 'processing';
                dispatch(postAddBulkAadUsers(bulkRetailUsersList[rowIndex]));
            }
            rowIndex++;
        });
    };

    function getBulkUsersExportData(filteredUsersData: BulkRetailUserType[]) {
        const exportDataList: ExportRetailUser[] = [];
        filteredUsersData.forEach((userData: any) => {
            if (userData.inviteRedeemUrl?.trim() !== '') {
                const exportData: ExportRetailUser = {
                    firstName: userData.retailUser.firstName,
                    lastName: userData.retailUser.lastName,
                    email: userData.retailUser.email,
                    roleName: userData.retailUser.roleName,
                    channelName: userData.retailUser.channelName,
                    tierName: userData.retailUser.tierName.trim(),
                    soldToCustomerNbr: userData.retailUser.soldToCustomerNbr,
                    shiptoCustomerNbr: userData.retailUser.shiptoCustomerNbr,
                    isWarrantyUpdateEnabled: userData.retailUser.isWarrantyUpdateEnabled ? 'Enabled' : 'Disabled',
                    isActive: userData.retailUser.isActive ? 'Active' : 'In Active',
                    errorInfo: userData.errorInfo !== '' ? userData.errorInfo : '',
                    inviteRedeemUrl: userData.retailUser.inviteRedeemUrl,
                };
                exportDataList.push(exportData);
            }
        });
        return exportDataList;
    }

    const exportUsersResults = () => {
        if (bulkRetailUsersList && bulkRetailUsersList.length > 0) {
            const exportUsersData = getBulkUsersExportData(bulkRetailUsersList);
            exportUsersData.unshift(exportExcelColumnHeader);
            const ws = XLSX.utils.json_to_sheet(exportUsersData, { skipHeader: true });
            const wb = { Sheets: { 'Retail Users List': ws }, SheetNames: ['Retail Users List'] };
            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const data = new Blob([excelBuffer], { type: exportFileType });
            FileSaver.saveAs(data, exportFileName);
        }
    };

    return (
        <div className={Styles.dvUploadSection}>
            {bulkRetailUsersList.length > 0 && <button className={Styles.uploadButton} onClick={displayModal}>Upload</button>}
            <Modal isOpen={showModal} isBlocking={true} onDismiss={closeModal}>
                <div className={Styles.dvModal}>
                    <div className={Styles.dvModalHeader}>
                        <span>{t('Bulk Users Data')}</span>
                        <a href="#" role="button" className={Styles.modalCloseIcon} onClick={closeModal}>
                            <Icon iconName="Cancel" />
                        </a>
                    </div>
                    <div className={Styles.dvModalBody}>
                        <div className={Styles.gridSection}>
                            <table className={Styles.tableNonBordered} role="grid">
                                <thead>
                                    <tr role="row">
                                        <th role="gridcell" style={{ width: 40, textAlign: 'center' }}>
                                            <div style={{ width: 40, textAlign: 'center' }}>Sl.No</div>
                                        </th>
                                        {getColumnHeadersList(t).map((column: any, index: number) => {
                                            { return getColumnHeader(index, column); }
                                        })}
                                        <th role="gridcell" style={{ width: 60, textAlign: 'center' }}>
                                            <div style={{ width: 60, textAlign: 'center' }}>Action</div>
                                        </th>
                                        <th role="gridcell" style={{ width: 100, textAlign: 'center' }}>
                                            <div style={{ width: 100, textAlign: 'center' }}>Status</div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr role="row" className={bulkRetailUsersList.length > 0 ? Styles.hidden : ''}>
                                        <td role="gridcell" className={Styles.tdNoRecordFound}>
                                            {t('no_records_found')}
                                        </td>
                                    </tr>
                                    {(bulkRetailUsersList && bulkRetailUsersList.length > 0) ? bulkRetailUsersList.map((element: any, index: number) => (
                                        <tr key={index} role="row">
                                            <td role="gridcell" style={{ width: 40 }}>
                                                <div style={{ width: 40, textAlign: 'center' }}>{index + 1}</div>
                                            </td>
                                            {getColumnHeadersList(t).map((column: any, innerIndex: number) => {
                                                { return getColumnContent(innerIndex, column, element); }
                                            })}
                                            <td role="gridcell" style={{ width: 60 }}>
                                                <div className={Styles[element.action.toLowerCase()]} style={{ width: 60, textAlign: 'center' }}>{element.action}</div>
                                            </td>
                                            <td role="gridcell" style={{ width: 100, textAlign: 'center' }}>
                                                <div className={element.status === '' && element.errorInfo !== '' ? Styles.errorStatusText : Styles.hidden}>
                                                    <TooltipHost
                                                        hostClassName={Styles.modalToolTip}
                                                        aria-label={element.errorInfo}
                                                        content={element.errorInfo}>
                                                        {element.errorInfo.length > 15 ? `${element.errorInfo.substring(0, 12)}...` : element.errorInfo}
                                                    </TooltipHost>
                                                </div>
                                                <div className={element.status === '' && element.errorInfo !== '' ? Styles.hidden : ''} style={{ width: 100, textAlign: 'center' }}>
                                                    <div className={element.status === 'processing' ? Styles.processing : Styles.hidden}>
                                                        <span className={Styles.hidden}>{element.errorInfo}</span>
                                                    </div>
                                                    <TooltipHost
                                                        hostClassName={element.status === 'success' ? Styles.modalToolTip : Styles.hidden}
                                                        aria-label={t('user_created_successfully')}
                                                        content={getStatusSuccessContent(element)}>
                                                        <Icon className={element.status === 'success' ? Styles.successIcon : Styles.hidden} iconName="CompletedSolid" />
                                                    </TooltipHost>
                                                    <TooltipHost
                                                        hostClassName={element.status === 'error' ? Styles.modalToolTip : Styles.hidden}
                                                        aria-label={t('error_user_exist_on_tenant')}
                                                        content={element.errorInfo !== '' ? element.errorInfo : t('error_user_exist_on_tenant')}>
                                                        {/* <Icon className={Styles.errorIcon} iconName="CriticalErrorSolid" /> */}
                                                        <Icon className={element.status === 'error' ? Styles.errorIcon : Styles.hidden} iconName="StatusErrorFull" />
                                                    </TooltipHost>
                                                    <TooltipHost
                                                        hostClassName={element.status === 'warning' ? Styles.modalToolTip : Styles.hidden}
                                                        aria-label={element.errorInfo !== '' ? element.errorInfo : t('error_user_exist_on_tenant')}
                                                        content={element.errorInfo !== '' ? element.errorInfo : t('error_user_exist_on_tenant')}>
                                                        {/* <Icon className={Styles.errorIcon} iconName="CriticalErrorSolid" /> */}
                                                        <Icon className={element.status === 'warning' ? Styles.warningIcon : Styles.hidden} iconName="WarningSolid" />
                                                    </TooltipHost>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : ''}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className={Styles.dvModalFooter}>
                        <button className={clsx(Styles.buttonSecondary, Styles.button)} onClick={closeModal}>
                            {t('Close')}
                        </button>
                        {(validRowsCount && validRowsCount > 0 && processingStatus !== 2) ?
                            <button className={clsx(Styles.buttonPrimary, Styles.button, Styles.mainButton)} onClick={processBulkRetailUsersList} disabled={processingStatus === 1}>
                                {t('Process')}
                            </button>
                            : ''}
                        {(validRowsCount && validRowsCount > 0 && processingStatus === 2 && showExportOption) ?
                            <button className={clsx(Styles.buttonPrimary, Styles.button, Styles.mainButton)} onClick={exportUsersResults}>
                                {t('Export')}
                            </button> : ''
                        }
                    </div>
                </div>
            </Modal>
        </div>
    );
}